import firebase from 'firebase/app';
import 'firebase/auth';
import 'firebase/firestore';
import 'firebase/storage';

import { firebaseConfig } from './config';
import { Observable, Observer } from 'rxjs';
import { Action } from 'redux';
import { Disposable } from '../types';
import { bufferTime, filter } from 'rxjs/operators';

export const firebaseApp = firebase.initializeApp(firebaseConfig);
export const firebaseAuth = firebase.auth();
export const firebaseStore = firebase.firestore();
firebaseStore.settings({ timestampsInSnapshots: false });
export const firebaseStorage = firebase.storage();
export const facebookProvider = new firebase.auth.FacebookAuthProvider();
export const googleProvider = new firebase.auth.GoogleAuthProvider();
export const emailProvider = new firebase.auth.EmailAuthProvider();
/*** providerId is always 'firebase' so get it from providerData instead ***/
export const getCurrentProviderId = ({ providerData }: firebase.User) => {
    const firstProvider = providerData[0]; // by convention its the first one...
    if (!firstProvider) {
        return null;
    }
    return firstProvider.providerId;
};
export const firebaseFunctionsUrl = `https://us-central1-${firebaseConfig.projectId}.cloudfunctions.net`;

export const subscribeToQuerySnapshotChanges =
    (query: firebase.firestore.Query): Observable<firebase.firestore.DocumentChange> => {
        return Observable.create((observer: Observer<firebase.firestore.DocumentChange>) => {
            const unsubcribe = query.onSnapshot(
                snapshot => snapshot.docChanges().forEach(change => observer.next(change)),
                err => observer.error(err),
                () => observer.complete());
            return () => unsubcribe();
        });
    };

export const dispatchChanges =
    (
        collectionPath: string,
        dispatch: { (action: Action): void },
        onNext: { (change: firebase.firestore.DocumentChange): Action },
        onError?: { (error: Error): Action }
    ): Disposable => {

        console.log('dispatchChanges', collectionPath);

        return firebaseStore.collection(collectionPath).onSnapshot(
            snapshot => snapshot.docChanges().forEach(change => dispatch(onNext(change))),
            error => { if (onError) { dispatch(onError(error)); } });
    };

export const observeChanges = (collectionPath: string): Observable<firebase.firestore.DocumentChange> =>
    Observable.create((observer: Observer<firebase.firestore.DocumentChange>) => {
        console.log('observeChanges', collectionPath);
        const unsubscribe = firebaseStore.collection(collectionPath).onSnapshot(
            snapshot => snapshot.docChanges().forEach(c => observer.next(c)),
            error => observer.error(new Error(`couldn't observeChanges on ${collectionPath}: ${error.message}`)),
            () => observer.complete());
        return () => {
            console.log('observeChanges', collectionPath, 'ended');
            unsubscribe();
        };
    });

export const observeChangesQuery = (collectionPath: string, field: string, op: string, value: string): Observable<firebase.firestore.DocumentChange> =>
    Observable.create((observer: Observer<firebase.firestore.DocumentChange>) => {
        console.log('observeChanges', collectionPath, field, op, value);
        const unsubscribe = firebaseStore.collection(collectionPath).onSnapshot(
            snapshot => snapshot.docChanges().forEach(c => observer.next(c)),
            error => observer.error(error),
            () => observer.complete());
        return () => {
            console.log('observeChanges', collectionPath, field, op, value, 'ended');
            unsubscribe();
        };
    });

export const observeChangesDoc = (path: string): Observable<firebase.firestore.DocumentSnapshot> =>
    Observable.create((observer: Observer<firebase.firestore.DocumentSnapshot>) => {
        console.log('observeChangesDoc', path);
        const unsubscribe = firebaseStore.doc(path).onSnapshot(
            snapshot => observer.next(snapshot),
            error => observer.error(new Error(`couldn't observeChangesDoc on ${path}: ${error.message}`)),
            () => observer.complete());
        return () => {
            console.log('observeChangesDoc', path, 'ended');
            unsubscribe();
        };
    });

export const observeChangesAll = (collectionPath: string): Observable<firebase.firestore.DocumentSnapshot[]> =>
    Observable.create((observer: Observer<firebase.firestore.DocumentSnapshot[]>) => {
        console.log('observeChangesAll', collectionPath);
        const unsubscribe = firebaseStore.collection(collectionPath).onSnapshot(
            snapshot => observer.next(snapshot.docs),
            error => observer.error(new Error(`couldn't observeChangesAll on ${collectionPath}: ${error.message}`)),
            () => observer.complete());
        return () => {
            console.log('observeChangesAll', collectionPath, 'ended');
            unsubscribe();
        };
    });

export const observeChangesAllBuffered = (path: string, timeMs: number) =>
    subscribeToQuerySnapshotChanges(firebaseStore.collection(path))
        .pipe(
            bufferTime(timeMs),
            filter(changes => changes.length > 0));